import { useState } from "react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Check, X, Loader2, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import type { TaskDeletionRequest } from "@/hooks/useTaskDeletionRequests";

interface TaskDeletionRequestsPanelProps {
  requests: TaskDeletionRequest[];
  onApprove: (id: string) => Promise<void>;
  onReject: (id: string) => Promise<void>;
  onReviewed?: () => void;
}

export function TaskDeletionRequestsPanel({
  requests,
  onApprove,
  onReject,
  onReviewed,
}: TaskDeletionRequestsPanelProps) {
  const { toast } = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  if (requests.length === 0) return null;

  const handle = async (req: TaskDeletionRequest, doApprove: boolean) => {
    try {
      setBusyId(req.id);
      if (doApprove) await onApprove(req.id);
      else await onReject(req.id);
      toast({
        title: doApprove ? "Tarefa excluída" : "Solicitação rejeitada",
      });
      onReviewed?.();
    } catch (err: unknown) {
      toast({
        title: "Erro ao revisar solicitação",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="p-4 border-amber-500/40 bg-amber-500/5">
      <div className="flex items-center gap-2 mb-3">
        <Trash2 className="h-4 w-4 text-amber-600" />
        <h3 className="text-sm font-semibold">
          Solicitações de exclusão ({requests.length})
        </h3>
      </div>
      <div className="space-y-2">
        {requests.map((req) => (
          <div
            key={req.id}
            className="flex items-start justify-between gap-3 rounded-md border bg-background px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">
                {req.task?.title ?? "Tarefa"}
              </p>
              <p className="text-xs text-muted-foreground">
                Solicitado por {req.requester?.display_name ?? "membro"} ·{" "}
                {format(parseISO(req.created_at), "dd/MM 'às' HH:mm", { locale: ptBR })}
              </p>
              {req.reason && (
                <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">
                  Motivo: {req.reason}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-emerald-600 hover:text-emerald-700"
                disabled={busyId === req.id}
                onClick={() => void handle(req, true)}
                title="Aprovar e excluir"
              >
                {busyId === req.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                disabled={busyId === req.id}
                onClick={() => void handle(req, false)}
                title="Rejeitar"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
